/**
 * Notification Sound Utility
 * Creates and plays a buzzer sound when tasks are completed
 */

class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.isPlaying = false;
    this.stopCallback = null;
  }

  // Initialize audio context
  initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  // Create a buzzer tone
  createBuzzerTone(frequency = 800, duration = 0.5, volume = 0.3) {
    const audioContext = this.initAudioContext();

    // Create oscillator for the buzzer sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    // Connect oscillator to gain node to audio output
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Set buzzer frequency and type
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    oscillator.type = 'square'; // Square wave for buzzer-like sound

    // Set volume envelope
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    // Play the sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);

    return { oscillator, gainNode };
  }

  // Play a single buzzer beep
  playBeep(frequency = 800, duration = 0.3, volume = 0.3) {
    try {
      return this.createBuzzerTone(frequency, duration, volume);
    } catch (error) {
      console.error('Error playing beep:', error);
      // Fallback to system beep if available
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        // Alternative fallback using speech synthesis
        const utterance = new SpeechSynthesisUtterance(' ');
        utterance.volume = 0.1;
        utterance.rate = 10;
        window.speechSynthesis.speak(utterance);
      }
    }
  }

  // Play completion notification (multiple beeps)
  async playCompletionNotification() {
    if (this.isPlaying) return;

    this.isPlaying = true;

    try {
      console.log('🔔 Playing completion notification');

      // Play three ascending beeps
      this.playBeep(600, 0.2, 0.3); // Low beep
      await this.delay(300);

      this.playBeep(800, 0.2, 0.3); // Medium beep
      await this.delay(300);

      this.playBeep(1000, 0.4, 0.4); // High beep (longer)
      await this.delay(600);

    } catch (error) {
      console.error('Error playing completion notification:', error);
    } finally {
      this.isPlaying = false;
    }
  }

  // Play looping buzzer until stopped
  playLoopingBuzzer(frequency = 800, interval = 1000, volume = 0.3) {
    if (this.isPlaying) {
      this.stopLoopingBuzzer();
    }

    this.isPlaying = true;
    console.log('🔔 Starting looping buzzer notification');

    const playLoop = () => {
      if (!this.isPlaying) return;

      this.playBeep(frequency, 0.3, volume);
      this.stopCallback = setTimeout(playLoop, interval);
    };

    // Start immediately
    playLoop();

    // Return a function to stop the loop
    return () => this.stopLoopingBuzzer();
  }

  // Stop looping buzzer
  stopLoopingBuzzer() {
    if (this.stopCallback) {
      clearTimeout(this.stopCallback);
      this.stopCallback = null;
    }
    this.isPlaying = false;
    console.log('🔇 Stopped looping buzzer notification');
  }

  // Utility delay function
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Play success fanfare
  async playSuccessFanfare() {
    if (this.isPlaying) return;

    this.isPlaying = true;

    try {
      console.log('🎉 Playing success fanfare');

      // Success melody - ascending notes
      const notes = [
        { freq: 523, duration: 0.15 }, // C5
        { freq: 659, duration: 0.15 }, // E5
        { freq: 784, duration: 0.15 }, // G5
        { freq: 1047, duration: 0.3 }, // C6
      ];

      for (const note of notes) {
        this.playBeep(note.freq, note.duration, 0.4);
        await this.delay(note.duration * 1000 + 50);
      }

    } catch (error) {
      console.error('Error playing success fanfare:', error);
    } finally {
      this.isPlaying = false;
    }
  }

  // Check if audio is supported
  isAudioSupported() {
    return !!(window.AudioContext || window.webkitAudioContext);
  }
}

// Export singleton instance
const notificationSound = new NotificationSound();

export default notificationSound;

// Export individual functions for convenience
export const playBeep = (frequency, duration, volume) =>
  notificationSound.playBeep(frequency, duration, volume);

export const playCompletionNotification = () =>
  notificationSound.playCompletionNotification();

export const playLoopingBuzzer = (frequency, interval, volume) =>
  notificationSound.playLoopingBuzzer(frequency, interval, volume);

export const stopLoopingBuzzer = () =>
  notificationSound.stopLoopingBuzzer();

export const playSuccessFanfare = () =>
  notificationSound.playSuccessFanfare();